<template>
  <v-container>
    <v-tabs v-model="tab">
      <v-tab>{{ $t('meta.genres') }}</v-tab>
      <v-tab>{{ $t('meta.categories') }}</v-tab>
    </v-tabs>

    <v-window v-model="tab">
      <v-window-item>
        <v-data-table
          :headers="genreHeaders"
          :items="genres"
          :loading="loading"
          :items-per-page="100"
          class="elevation-1"
        >
          <template v-slot:top>
            <v-toolbar flat density="compact" class="px-4">
              <v-toolbar-title>{{ $t('meta.genres') }}</v-toolbar-title>
              <v-spacer></v-spacer>
              <div class="d-flex align-center">
                <span class="mr-4">{{ $t('meta.genres') }}: {{ genres.length }}</span>
                <add-genre @genre-added="fetchGenres" />
              </div>
            </v-toolbar>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex ga-2 justify-center">
              <v-btn
                icon
                color="primary"
                size="small"
                @click="editGenreFun(item)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                icon
                color="error"
                size="small"
                @click="deleteGenre(item)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-window-item>

      <v-window-item>
        <v-data-table
          :headers="categoryHeaders"
          :items="categories"
          :loading="loading"
          :items-per-page="100"
          class="elevation-1"
          :row-props="getCategoryRowProps"
        >
          <template v-slot:top>
            <v-toolbar flat density="compact" class="px-4">
              <v-toolbar-title>{{ $t('meta.categories') }}</v-toolbar-title>
              <v-spacer></v-spacer>
              <div class="d-flex align-center">
                <span class="mr-4">{{ $t('meta.categories') }}: {{ categories.length }}</span>
                <add-category @category-added="fetchCategories" />
              </div>
            </v-toolbar>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex ga-2 justify-center">
              <v-btn
                icon
                color="primary"
                size="small"
                @click="editCategoryFun(item)"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                icon
                color="error"
                size="small"
                @click="deleteCategory(item)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-window-item>
    </v-window>

    <v-dialog v-model="editGenreDialog" max-width="500px">
      <edit-genre
        v-if="editGenreDialog"
        :genre="selectedGenre"
        @close="editGenreDialog = false"
        @genre-updated="fetchGenres"
      />
    </v-dialog>

    <v-dialog v-model="editCategoryDialog" max-width="500px">
      <edit-category
        v-if="editCategoryDialog"
        :category="selectedCategory"
        @close="editCategoryDialog = false"
        @category-updated="fetchCategories"
      />
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import AddGenre from '@/components/AddGenre.vue'
import AddCategory from '@/components/AddCategory.vue'
import EditGenre from '@/components/EditGenre.vue'
import EditCategory from '@/components/EditCategory.vue'
import { useI18n } from 'vue-i18n'
import { nextTick } from 'vue'


const { t: $t } = useI18n()
const tab = ref(null)
const genres = ref([])
const categories = ref([])
const loading = ref(true)
const editGenreDialog = ref(false)
const editCategoryDialog = ref(false)
const selectedGenre = ref({})
const selectedCategory = ref({})

const genreHeaders = computed(() => [
    { title: $t('meta.genreId'), key: 'id' },
  { title: $t('meta.name'), key: 'name' },
  { title: $t('meta.index'), key: 'genre_index' },
  { title: $t('meta.code'), key: 'code' },
  { title: $t('meta.actions'), key: 'actions', sortable: false }
])

const categoryHeaders = computed(() => [
     { title: $t('meta.categoryId'), key: 'id' },
     { title: $t('meta.genreId'), key: 'genre_id' },
  { title: $t('meta.name'), key: 'name' },
  { title: $t('meta.index'), key: 'category_index' },

     { title: $t('meta.code'), key: 'code' },
  { title: $t('meta.criteriaMapping'), key: 'criteria_mapping' },
  { title: $t('meta.actions'), key: 'actions', sortable: false }
])

onMounted(async () => {
  await fetchGenres()
  await fetchCategories()
})

async function fetchGenres() {
  loading.value = true
  try {
    const response = await axios.get('/api/genres')
    genres.value = response.data
  } catch (error) {
    console.error('Error fetching genres:', error)
  } finally {
    loading.value = false
  }
}

async function fetchCategories() {
  loading.value = true
  try {
    const response = await axios.get('/api/categories')
    categories.value = response.data
  } catch (error) {
    console.error('Error fetching categories:', error)
  } finally {
    loading.value = false
  }
}

function editGenreFun(item) {
  console.log('editGenre', item)
  selectedGenre.value = { ...item }
  editGenreDialog.value = true
}

function editCategoryFun(item) {
  selectedCategory.value = { ...item }
  editCategoryDialog.value = true
}

// Color palette for genre grouping
const genreColors = [
  '#E3F2FD', // Light Blue
  '#F3E5F5', // Light Purple
  '#E8F5E9', // Light Green
  '#FFF3E0', // Light Orange
  '#FCE4EC', // Light Pink
  '#F1F8E9', // Light Lime
  '#E0F2F1', // Light Teal
  '#FFF9C4', // Light Yellow
]

function getCategoryRowProps({ item }) {
  const genreIndex = item.genre_id % genreColors.length
  return {
    style: `background-color: ${genreColors[genreIndex]};`
  }
}

async function deleteGenre(item) {
  if (confirm($t('meta.confirmDeleteGenre'))) {
    try {
      await axios.delete(`/api/genres/${item.id}`)
      await fetchGenres()
    } catch (error) {
      console.error('Error deleting genre:', error)
    }
  }
}

async function deleteCategory(item) {
  if (confirm($t('meta.confirmDeleteCategory'))) {
    try {
      await axios.delete(`/api/categories/${item.id}`)
      await fetchCategories()
    } catch (error) {
      console.error('Error deleting category:', error)
    }
  }
}
</script>
